import { Collection } from './../types/collection';
import { reduce } from '../collections/reduce';
import { _merge, TMergeFn } from './_merge';
import { is } from '../util/is';

/**
 * Deep merge two objects with a reconciliation rule.
 * A standard merge will is the same as interleave, which
 * is also the failover rule in this function.
 *
 * - clobber will override, but still deep merge, an array on top of an array
 * - append will concatenate two arrays
 * - interleave (failover) is the same as a merge
 * - a custom function is usually unnecessary
 *
 * @param fn A merge rule or 'append', 'clobber', 'interleave'.
 * @param dest The object onto which to merge properties from `sources`.
 * @param sources The objects whose properties should be merged with `dest`.
 */
export function mergeWith<T1, T2>(fn: TMergeFn | t_merge_fn, dest: T1, source: T2): T1 & T2;
export function mergeWith<T1, T2>(fn: TMergeFn | t_merge_fn, dest: T1[], source: T2[]): (T1 & T2)[];
export function mergeWith<T1, T2, T3>(fn: TMergeFn | t_merge_fn, dest: T1, source: T2, source2: T3): T1 & T2 & T3;
export function mergeWith<T1, T2, T3>(
  fn: TMergeFn | t_merge_fn,
  dest: T1[],
  source: T2[],
  source2: T3[]
): (T1 & T2 & T3)[];
export function mergeWith<T1, T2, T3, T4>(
  fn: TMergeFn | t_merge_fn,
  dest: T1,
  source: T2,
  source2: T3,
  source3: T4
): T1 & T2 & T3 & T4;
export function mergeWith<T1, T2, T3, T4>(
  fn: TMergeFn | t_merge_fn,
  dest: T1[],
  source: T2[],
  source2: T3[],
  source3: T4[]
): (T1 & T2 & T3 & T4)[];
export function mergeWith<T1, T2, T3, T4, T5>(
  fn: TMergeFn | t_merge_fn,
  dest: T1,
  source: T2,
  source2: T3,
  source3: T4,
  source4: T5
): T1 & T2 & T3 & T4 & T5;
export function mergeWith<T1, T2, T3, T4, T5>(
  fn: TMergeFn | t_merge_fn,
  dest: T1[],
  source: T2[],
  source2: T3[],
  source3: T4[],
  source4: T5[]
): (T1 & T2 & T3 & T4 & T5)[];
export function mergeWith(
  fn: TMergeFn | t_merge_fn,
  dest: Collection<any>,
  ...sources: Collection<any>[]
): Collection<any> {
  const _fn = !is(String, fn) ? fn : mergeRules[fn] || mergeRules['interleave'];

  return reduce(_merge.bind(undefined, _fn), dest, sources);
}

type t_merge_fn = 'append' | 'clobber' | 'interleave';

const mergeRules: Record<t_merge_fn, TMergeFn> = {
  interleave: (k: unknown, xs: unknown, ys: unknown) => undefined,
  append: (k: unknown, xs: unknown, ys: unknown) => {
    return Array.isArray(xs) && Array.isArray(ys) ? xs.concat(ys) : undefined;
  },
  clobber: (k: unknown, xs: unknown, ys: unknown) => {
    if (Array.isArray(xs) && Array.isArray(ys)) {
      xs.length = ys.length;
      return ys;
    }

    return undefined;
  }
};
